import { optionService } from "../option";
import { CommandWithFollowup, LayoutsState, QuestionStepData, Step, StepService, VariantResource } from "../../types";
import { WorkflowManager } from "../../WorkflowManager";

class QuestionStepService implements StepService<QuestionStepData> {
    async init(
        stepData: Step<QuestionStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        const option = stepData.option;
        if (!option) {
            return null;
        }
        if (reducerState) {
            await this.reload(stepData, workflowManager, reducerState);
        } else {
            const variants = option.variants;
            const defaultVariant = optionService.getDefaultVariant(option);
            if (defaultVariant) {
                const variant = variants?.find((variant: VariantResource) => variant.id === option.defaultVariant?.id);
                return this.selectVariantLambda(stepData, variant?.id || "", workflowManager);
            }
        }
        return null;
    }

    private async reload(
        stepData: Step<QuestionStepData>,
        workflowManager: WorkflowManager,
        reducerState: LayoutsState,
    ) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const option = stepData.option;
        if (serializedStep?.selectedVariants) {
            const variantId = serializedStep.selectedVariants[0].id;
            if (option && variantId) {
                const variant = option.variants?.find((variant) => variant.id === variantId);
                if (variant) {
                    await workflowManager.setSelectionsAndElements(stepData.stepName, [variant], []);
                }
            }
        }
    }

    async selectVariant(stepData: Step<QuestionStepData>, variantId: string, workflowManager: WorkflowManager) {
        // Ensure workflow is initialized prior to making a selection.
        await workflowManager.getInitializationPromise();
        // Select variant.
        const result = await this.selectVariantLambda(stepData, variantId, workflowManager);
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    private async selectVariantLambda(
        stepData: Step<QuestionStepData>,
        variantId: string,
        workflowManager: WorkflowManager,
    ): Promise<CommandWithFollowup | null> {
        const option = stepData.option;
        if (!option) {
            return null;
        }
        const variants = option.variants;
        if (!variants) {
            return null;
        }
        const variant =
            variants.length > 1 ? variants.find((variant: VariantResource) => variant.id === variantId) : variants[0];
        if (!variant) {
            throw new Error(`Failed to find selected variant ${variantId} in option ${option.id} of step ${stepData.stepName} (${stepData.stepTitle})`);
        }

        return {
            command: undefined,
            followup: async () => {
                await workflowManager.setSelectionsAndElements(stepData.stepName, [variant], [], async () => {
                    workflowManager.setMandatoryFulfilled(stepData.stepName, true);
                });
            },
        };
    }
}

export const questionStepService = new QuestionStepService();
